import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import { TableSearch } from "../../components/Table/TableActions";
import Badge from "../../components/Badge/Badge";
import Modal from "../../components/Modal";
import Button from "../../components/Button/Button";
import { Icon } from "@iconify/react";
import { toast, ToastContainer } from "react-toastify";
import { httprequest } from "../../data/api";
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import InputGroup from "../../components/FormComponent/InputGroup";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import TextAreaGroup from "../../components/FormComponent/TextAreaGroup";
import styles from "./questionpool.module.css";
import nosearch from "../../assets/images/no-search.svg";
import NotFounditem from "../error/NotFound";

const QUESTIONS_ENDPOINT = "/admin/v1/past-questions/questions";
const QUESTION_POOL_LIMIT = 21;

const generateSlug = (text) => (text || "").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const OPTION_KEYS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const QuestionPool = () => {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({ examYear: "", examType: "", subject: "", topic: "", status: "" });

  const [selectedIds, setSelectedIds] = useState([]);
  const [cardPage, setCardPage] = useState(1);
  const CARD_PER_PAGE = QUESTION_POOL_LIMIT;

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editOptions, setEditOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const uniqueYears = [...new Set(questions.map((q) => q.year).filter(Boolean))].sort((a, b) => b - a);
  const uniqueExamTypes = [...new Set(questions.map((q) => q.exam_type).filter(Boolean))];
  const uniqueSubjects = [...new Set(questions.map((q) => q.subject).filter(Boolean))];
  const uniqueTopics = [...new Set(questions.map((q) => q.topic_category).filter(Boolean))];

  const fetchQuestions = () => {
    setIsLoading(true);
    httprequest
      .get(QUESTIONS_ENDPOINT, { params: { page: 1, limit: QUESTION_POOL_LIMIT } })
      .then((res) => {
        const payload = res?.data?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        setQuestions(list);
        setSearchData(list);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load questions");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    let result = [...questions];
    if (filters.examYear) result = result.filter((q) => String(q.year) === String(filters.examYear));
    if (filters.examType) result = result.filter((q) => q.exam_type === filters.examType);
    if (filters.subject) result = result.filter((q) => q.subject === filters.subject);
    if (filters.topic) result = result.filter((q) => q.topic_category === filters.topic);
    if (filters.status) result = result.filter((q) => q.status === filters.status);
    setSearchData(result);
    setCardPage(1);
  }, [filters, questions]);

  const handleSearch = (e) => {
    const q = (e.target.value || "").toLowerCase();
    if (!q) {
      setSearchData(questions);

      return;
    }
    const result = questions.filter((item) => (
      (item.subject || "").toLowerCase().includes(q) ||
      (item.question?.text || "").toLowerCase().includes(q) ||
      (item.exam_type || "").toLowerCase().includes(q) ||
      (item.topic_category || "").toLowerCase().includes(q)
    ));
    setSearchData(result);
  };

  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const openEditModal = (question) => {
    const opts = question.question?.options || {};
    setEditForm({
      id: question.id,
      question_text: question.question?.text || "",
      subject: question.subject || "",
      exam_type: question.exam_type || "WAEC",
      year: question.year || "",
      category: question.category || "",
      topic_category: question.topic_category || "",
      section_type: question.section_type || "objective",
      status: question.status || "draft",
      correct_answer: question.question?.correct_answer || ""
    });
    setEditOptions(
      Object.entries(opts).map(([key, text]) => ({
        key,
        text,
        isCorrect: key === question.question?.correct_answer
      }))
    );
    setEditModal(true);
    setAnchorEl(null);
  };

  const handleEditSave = () => {
    if (!editForm) return;
    setIsSubmitting(true);
    const optionsObj = {};
    editOptions.forEach((o) => {
      optionsObj[o.key] = o.text;
    });
    const body = {
      subject: editForm.subject,
      subject_slug: generateSlug(editForm.subject),
      exam_type: editForm.exam_type,
      year: Number(editForm.year),
      category: editForm.category,
      topic_category: editForm.topic_category,
      topic_category_slug: generateSlug(editForm.topic_category),
      section_type: editForm.section_type,
      status: editForm.status,
      question: {
        text: editForm.question_text,
        options: optionsObj,
        correct_answer: editForm.correct_answer,
        subparts: null
      }
    };
    httprequest
      .patch(`${QUESTIONS_ENDPOINT}/${editForm.id}`, body)
      .then(() => {
        toast.success("Question updated successfully");
        setEditModal(false);
        fetchQuestions();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to update question"))
      .finally(() => setIsSubmitting(false));
  };

  const openDeleteDialog = (question) => {
    setDeleteTarget(question);
    setDeleteDialog(true);
    setAnchorEl(null);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    httprequest
      .delete(`${QUESTIONS_ENDPOINT}/${deleteTarget.id}`)
      .then(() => {
        toast.success("Question deleted");
        setDeleteDialog(false);
        setDeleteTarget(null);
        fetchQuestions();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to delete question"))
      .finally(() => setIsDeleting(false));
  };

  const handleBulkAction = (action) => {
    if (selectedIds.length === 0) {
      toast.info("Select at least one question");

      return;
    }
    const promises = selectedIds.map((id) => httprequest.patch(`${QUESTIONS_ENDPOINT}/${id}`, { status: action === "publish" ? "published" : "draft" }));
    Promise.all(promises)
      .then(() => {
        toast.success(`Questions ${action}ed successfully`);
        setSelectedIds([]);
        fetchQuestions();
      })
      .catch(() => toast.error("Some updates failed"));
  };

  const handleMenuOpen = (e, row) => {
    setAnchorEl(e.currentTarget);
    setMenuRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const totalPages = Math.ceil(searchData.length / CARD_PER_PAGE);
  const paginatedCards = searchData.slice((cardPage - 1) * CARD_PER_PAGE, cardPage * CARD_PER_PAGE);

  const FilterBar = (
    <div className={styles.filterBar}>
      <TableSearch placeholder="Search here..." searchTableFunc={handleSearch} />
      <div className={styles.filterGroup}>
        <select className={styles.filterSelect} value={filters.examType} onChange={(e) => handleFilterChange("examType", e.target.value)}>
          <option value="">Exam Type</option>
          {uniqueExamTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className={styles.filterSelect} value={filters.subject} onChange={(e) => handleFilterChange("subject", e.target.value)}>
          <option value="">Subject</option>
          {uniqueSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={styles.filterSelect} value={filters.examYear} onChange={(e) => handleFilterChange("examYear", e.target.value)}>
          <option value="">Exam year</option>
          {uniqueYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className={styles.filterSelect} value={filters.topic} onChange={(e) => handleFilterChange("topic", e.target.value)}>
          <option value="">Topic</option>
          {uniqueTopics.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    </div>
  );

  const editModalContent = editForm && (
    <div>
      <TextAreaGroup label="Question" value={editForm.question_text} onChange={(e) => setEditForm({ ...editForm, question_text: e.target.value })} placeHolder="Enter question text" />
      <InputGroup label="Subject" inputType="text" value={editForm.subject} onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })} />
      <div className={styles.formRow}>
        <SelectGroup label="Exam Type">
          <select value={editForm.exam_type} onChange={(e) => setEditForm({ ...editForm, exam_type: e.target.value })}>
            <option value="WAEC">WAEC</option>
            <option value="NECO">NECO</option>
            <option value="JAMB">JAMB</option>
            <option value="GCE">GCE</option>
          </select>
        </SelectGroup>
        <InputGroup label="Year" inputType="number" value={editForm.year} onChange={(e) => setEditForm({ ...editForm, year: e.target.value })} />
      </div>
      <div className={styles.formRow}>
        <InputGroup label="Subject" inputType="text" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
        <InputGroup label="Topic" inputType="text" value={editForm.topic_category} onChange={(e) => setEditForm({ ...editForm, topic_category: e.target.value })} />
      </div>
      <div className={styles.formRow}>
        <SelectGroup label="Add Answer Type *">
          <select value={editForm.section_type} onChange={(e) => setEditForm({ ...editForm, section_type: e.target.value })}>
            <option value="objective">Multi choice</option>
            <option value="theory">Theory</option>
          </select>
        </SelectGroup>
        <SelectGroup label="Status">
          <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
            <option value="draft">Draft</option>
            <option value="published">Publish</option>
          </select>
        </SelectGroup>
      </div>
      <p className={styles.answersTitle}>Answers</p>
      {editOptions.map((opt, idx) => (
        <div className={styles.optionRow} key={opt.key}>
          <div className={styles.optionInputWrapper}>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#101928", margin: "0 0 4px" }}>Option {opt.key}</p>
            <input
              className={styles.optionInput}
              value={opt.text}
              onChange={(e) => {
                const updated = [...editOptions];
                updated[idx].text = e.target.value;
                setEditOptions(updated);
              }}
            />
            <button className={styles.imgIcon}><Icon icon="fluent:image-16-regular" /></button>
          </div>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="editCorrect"
              checked={editForm.correct_answer === opt.key}
              onChange={() => setEditForm({ ...editForm, correct_answer: opt.key })}
            />
            Correct answer
          </label>
        </div>
      ))}
      <button
        className={styles.addOptionBtn}
        onClick={() => {
          const nextKey = OPTION_KEYS[editOptions.length] || `OPT${editOptions.length + 1}`;
          setEditOptions([...editOptions, { key: nextKey, text: "", isCorrect: false }]);
        }}
      >
        <Icon icon="fluent:add-circle-16-regular" /> Add option
      </button>
    </div>
  );

  return (
    <Layout>
      <PageTitle
        title="Question Pool"
        subtitle="All questions available across the platform"
        button={
          <Button
            variant="primary"
            buttonSize="fluid"
            color="btndefault"
            buttonName="Add Question"
            buttonIcon={<Icon icon="fluent:chevron-down-16-regular" style={{ marginLeft: 4 }} />}
            onClick={() => navigate("/question-pool/create")}
          />
        }
      />

      {FilterBar}
      <div className={styles.cardViewHeader}>
        <select
          className={styles.statusChip}
          value={filters.status}
          onChange={(e) => handleFilterChange("status", e.target.value)}
        >
          <option value="">All</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {searchData.length === 0 && !isLoading ? (
        <NotFounditem
          img={nosearch}
          title="No Question Available"
          subtitle="No question has been added. Reset your filter or add a question"
        />
      ) : (
        <>

          <div className={styles.cardContainer}>
            {paginatedCards.map((q) => {
              const opts = q.question?.options || {};
              const isSelected = selectedIds.includes(q.id);

              return (
                <div key={q.id} className={`${styles.questionCard} ${isSelected ? styles.selected : ""}`}>
                  <input
                    type="checkbox"
                    className={styles.cardCheckbox}
                    checked={isSelected}
                    onChange={() => toggleSelect(q.id)}
                  />
                  <div className={styles.cardContent}>
                    <div className={styles.cardHeaderRow}>
                      <p className={styles.cardQuestionText}>{q.question?.text || "-"}</p>
                      <Badge status={q.status?.toLowerCase()} />
                    </div>
                    <p className={styles.cardMeta}>
                      {q.subject}<span className={styles.cardMetaDot}>·</span>
                      {q.topic_category}<span className={styles.cardMetaDot}>·</span>
                      {q.year}
                    </p>
                    <div className={styles.answersGrid}>
                      {Object.entries(opts).map(([key, val]) => (
                        <span className={styles.answerItem} key={key}>
                          <span className={styles.answerKey}>{key}</span> {val}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.cardRight}>
                    <button className={styles.menuDotBtn} onClick={(e) => handleMenuOpen(e, q)}>
                      <Icon icon="ph:dots-three-vertical-bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.pageBtn} disabled={cardPage === 1} onClick={() => setCardPage((p) => p - 1)}>
                <Icon icon="fluent:chevron-left-16-regular" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${p === cardPage ? styles.active : ""}`}
                  onClick={() => setCardPage(p)}
                >
                  {p}
                </button>
              ))}
              <button className={styles.pageBtn} disabled={cardPage === totalPages} onClick={() => setCardPage((p) => p + 1)}>
                <Icon icon="fluent:chevron-right-16-regular" />
              </button>
            </div>
          )}

          {selectedIds.length > 0 && (
            <div className={styles.bulkBar}>
              <button className={styles.bulkPublish} onClick={() => handleBulkAction("publish")}>Publish</button>
              <button className={styles.bulkUnpublish} onClick={() => handleBulkAction("unpublish")}>Unpublish</button>
            </div>
          )}
        </>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
        PaperProps={{ elevation: 3, style: { borderRadius: 12, marginTop: 8, minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            openEditModal(menuRow);
            handleMenuClose();
          }}
          style={{ fontSize: 13, padding: "10px 20px", gap: 10 }}
        >
          <Icon icon="fluent:edit-16-regular" /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            openDeleteDialog(menuRow);
            handleMenuClose();
          }}
          style={{ fontSize: 13, padding: "10px 20px", gap: 10, color: "#CB1A14" }}
        >
          <Icon icon="fluent:delete-16-regular" /> Delete
        </MenuItem>
      </Menu>

      <Modal
        title="Edit Question"
        subtitle="Edit a question"
        isopen={editModal}
        closeModal={() => {
          if (!isSubmitting) setEditModal(false);
        }}
        modalContent={editModalContent}
        modalFooter={
          <Button
            variant="primary"
            buttonSize="full"
            color="btndefault"
            buttonName={isSubmitting ? "Saving..." : "Save"}
            onClick={handleEditSave}
            disabled={isSubmitting}
          />
        }
      />

      <Dialog
        open={deleteDialog}
        onClose={() => {
          if (!isDeleting) {
            setDeleteDialog(false);
            setDeleteTarget(null);
          }
        }}
        PaperProps={{ className: styles.deleteDialogPaper }}
      >
        <DialogTitle className={styles.deleteTitle}>Delete Question?</DialogTitle>
        <DialogContent><p className={styles.deleteContent}>This action will archive the question. It cannot be undone.</p></DialogContent>
        <DialogActions className={styles.deleteActions}>
          <button
            className={styles.cancelBtn}
            onClick={() => {
              setDeleteDialog(false);
              setDeleteTarget(null);
            }}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button className={styles.confirmDeleteBtn} onClick={handleDelete} disabled={isDeleting}>{isDeleting ? "Deleting..." : "Delete"}</button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </Layout>
  );
};

export default QuestionPool;
